import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Semantic status colours (docs/BRANDING_AND_DESIGN_SYSTEM.md). Red is only for real
 * problems; "none yet" is neutral. Status is always carried by the text too, never by
 * colour alone.
 */
export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const tones: Record<BadgeTone, { box: string; dot: string }> = {
  neutral: { box: 'bg-slate-100 text-slate-700 ring-slate-200', dot: 'bg-slate-400' },
  brand: { box: 'bg-brand-50 text-brand-800 ring-brand-200', dot: 'bg-brand-600' },
  success: { box: 'bg-emerald-50 text-emerald-800 ring-emerald-200', dot: 'bg-emerald-500' },
  warning: { box: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500' },
  danger: { box: 'bg-red-50 text-red-800 ring-red-200', dot: 'bg-red-500' },
  info: { box: 'bg-sky-50 text-sky-800 ring-sky-200', dot: 'bg-sky-500' },
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Adds a leading status dot. */
  dot?: boolean;
}

export function Badge({ tone = 'neutral', dot = false, className, children, ...rest }: BadgeProps) {
  const t = tones[tone];
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-semibold ring-1 ring-inset', t.box, className)}
      {...rest}
    >
      {dot && <span className={cn('size-1.5 rounded-full', t.dot)} aria-hidden />}
      {children}
    </span>
  );
}
